import { useMemo, useState } from "react";
import { Store, Link as LinkIcon, Building2 } from "lucide-react";

import { ClienteFilialDTO } from "@/types/cliente-filial.type";
import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { ActionMenu, ActionMenuItem } from "@/components/shared/ActionMenu";
import { PermissaoEnum } from "@workflow/types";

import { filiaisQueries, filiaisKeys } from "@/services/api/queries/filiais.queries";
import { FilialVincularDialog } from "./FilialVincularDialog";
import { format } from "date-fns";
import { PersonBadge } from "@/components/shared/PersonBadge";

interface FilialPendenteListTableProps {
  storageKey: string;
}

export function FilialPendenteListTable({ storageKey }: FilialPendenteListTableProps) {
  // `useFilialActions` (no FilialVincularDialog) invalida `filiais-pendentes-list`,
  // então vincular uma filial refresca esta lista sozinho.
  const { tableProps, isError } = useServerDataTable<
    ClienteFilialDTO,
    unknown,
    Record<string, unknown>
  >({
    queryOptions: filiaisQueries.pendentes,
    filters: filiaisQueries.filtros,
    invalidateKey: filiaisKeys.pendentes,
  });

  const [filialToLink, setFilialToLink] = useState<ClienteFilialDTO | null>(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  const columns = useMemo<Column<ClienteFilialDTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: true,
        width: "min-w-[40px]",
        render: (filial) => {
          const actions: ActionMenuItem[] = [
            {
              label: "Vincular à Cliente",
              icon: <LinkIcon className="h-4 w-4 text-emerald-500" />,
              onClick: () => {
                setFilialToLink(filial);
                setIsLinkDialogOpen(true);
              },
              permissions: [PermissaoEnum.ADMIN],
            },
          ];
          return <ActionMenu items={actions} />;
        },
      },
      {
        key: "filial.id",
        title: "ID",
        sortable: true,
        width: "w-[100px]",
        render: (filial) => <span className="font-medium text-primary">{filial.id}</span>,
        raw: (filial) => filial.id.toString(),
      },
      {
        key: "filial.idPubli",
        title: "ID Publi",
        sortable: true,
        width: "w-[100px]",
        render: (filial) => (
          <span className="font-medium text-primary dark:text-emerald-500">{filial.idPubli}</span>
        ),
        raw: (filial) => filial.idPubli.toString(),
      },
      {
        key: "filial.nome",
        title: "Nome da Filial",
        sortable: true,
        width: "min-w-[250px]",
        render: (filial) => (
          <div className="flex items-center gap-2 py-1">
            <Store className="max-h-4 min-h-4 min-w-4 max-w-4 text-muted-foreground opacity-70" />
            <span className="truncate font-semibold text-foreground" title={filial.nome}>
              {filial.nome}
            </span>
          </div>
        ),
        raw: (f) => f.nome,
      },
      {
        key: "filial.cnpj",
        title: "CNPJ",
        sortable: true,
        render: (filial) => <span className="text-sm">{filial.cnpj || "-"}</span>,
        raw: (f) => f.cnpj || "",
      },
      {
        key: "filial.grupo",
        title: "Grupo Informado",
        sortable: true,
        render: (filial) => (
          <span className="rounded-md bg-muted px-2 py-0.5 text-sm font-medium text-muted-foreground">
            {filial.grupo || "Nenhum"}
          </span>
        ),
        raw: (f) => f.grupo || "",
      },
      {
        key: "cliente.nome",
        title: "Cliente",
        sortable: true,
        render: (filial) => (
          <div className="flex items-center gap-2 py-1">
            <Building2 className="max-h-4 min-h-4 min-w-4 max-w-4 text-muted-foreground opacity-70" />
            <span className="truncate font-semibold text-foreground" title={filial.cliente?.nome}>
              {filial.cliente?.nome || "-"}
            </span>
          </div>
        ),
        raw: (f) => f.cliente?.nome || "",
      },
      {
        key: "criadoPor.nome",
        title: "Criado por",
        sortable: true,
        render: (cliente) =>
          cliente?.criadoPor ? <PersonBadge pessoa={cliente.criadoPor} isCompact={true} /> : "-",
        raw: (cliente) => cliente.criadoPor?.nome || "",
      },
      {
        key: "cliente.criadoEm",
        title: "Criado em",
        sortable: true,
        width: "min-w-[120px]",
        render: (cliente) => (
          <span className="text-sm text-muted-foreground">
            {cliente.criadoEm ? format(new Date(cliente.criadoEm), "dd/MM/yyyy HH:mm") : "-"}
          </span>
        ),
        raw: (cliente) => cliente.criadoEm?.toString() || "",
      },
    ],
    [],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={storageKey}
        columns={columns}
        emptyMessage={
          isError
            ? "Não foi possível carregar as filiais pendentes. Tente recarregar."
            : "Nenhuma filial pendente de vínculo."
        }
        defaultSortColumn="filial.id"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar por nome, cnpj ou grupo..."
        className="border-none"
      />

      <FilialVincularDialog
        open={isLinkDialogOpen}
        onOpenChange={(isOpen) => {
          setIsLinkDialogOpen(isOpen);
          if (!isOpen) setFilialToLink(null);
        }}
        filial={filialToLink}
      />
    </>
  );
}
